import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useGetOneTimerTasks, useUpdateTaskOrder } from '../api/oneTimersApi';
import OneTimerCard from '../components/OneTimerCard';
import AddOneTimerModal from '../components/AddOneTimerModal';
import { AnimatePresence } from 'framer-motion';
import DraggableTaskList from '../components/DraggableTaskList';
import confetti from 'canvas-confetti';

const OneTimersPage: React.FC = () => {
  const { data: tasks = [], isLoading } = useGetOneTimerTasks();
  const updateTaskOrderMutation = useUpdateTaskOrder();

  const [showDone, setShowDone] = useState(false);
  const prevTodoCountRef = useRef<number>(0);

  const todoTasks = tasks.filter((t) => !t.isDone);
  const doneTasks = tasks.filter((t) => t.isDone);

  // Calculate stats
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  const doneThisWeek = doneTasks.filter(
    (task) => task.completedAt && new Date(task.completedAt) >= weekAgo
  ).length;

  // Detect when last task is marked done and fire confetti
  useEffect(() => {
    if (prevTodoCountRef.current > 0 && todoTasks.length === 0 && doneTasks.length > 0) {
      // All tasks completed!
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
    }
    prevTodoCountRef.current = todoTasks.length;
  }, [todoTasks.length, doneTasks.length]);

  // Sort tasks by sortOrder from database
  const sortedTodoTasks = [...todoTasks].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  const handleReorder = (reorderedTasks: typeof todoTasks) => {
    // Update sortOrder for all tasks based on new position
    const taskUpdates = reorderedTasks.map((task, index) => ({
      id: task.id,
      sortOrder: index + 1,
    }));
    updateTaskOrderMutation.mutate(taskUpdates);
  };

  if (isLoading) {
    return <div className="p-8 text-center">Loading tasks...</div>;
  }

  return (
    <div className="bg-gray-50 min-h-screen w-full">
      <div className="max-w-3xl mx-auto p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">Tasks</h1>

        {/* Stats chip */}
        <div className="mb-6 flex items-center gap-2 text-sm text-gray-600">
          <span className="px-3 py-1 bg-white rounded-full shadow-sm border border-gray-200">
            {todoTasks.length} left
          </span>
          <span className="text-gray-400">•</span>
          <span className="px-3 py-1 bg-white rounded-full shadow-sm border border-gray-200">
            {doneThisWeek} done this week
          </span>
        </div>

        <AddOneTimerModal />

        {/* To-Do Section */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-gray-700 mb-3">To Do</h2>
          {todoTasks.length === 0 ? (
            <p className="text-gray-500">No tasks yet. Add one above!</p>
          ) : (
            <DraggableTaskList tasks={sortedTodoTasks} onReorder={handleReorder} />
          )}
        </section>

        {/* Done Section */}
        <section>
          <button
            onClick={() => setShowDone(!showDone)}
            className="w-full flex items-center justify-between px-4 py-3 bg-white border rounded-lg shadow-sm text-left hover:bg-gray-100 transition-colors"
          >
            <span className="font-semibold text-gray-700">Done ({doneTasks.length})</span>
            <svg
              className={`w-5 h-5 text-gray-500 transform transition-transform ${showDone ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showDone && (
            <AnimatePresence>
              <div className="mt-4 space-y-3">
                {doneTasks.length === 0 ? (
                  <p className="text-gray-500">No completed tasks yet.</p>
                ) : (
                  doneTasks.map((task) => <OneTimerCard key={task.id} task={task} />)
                )}
              </div>
            </AnimatePresence>
          )}
        </section>
      </div>
    </div>
  );
};

export default OneTimersPage; 